import Keycloak from 'keycloak-js'
import { runtimeConfig } from '../runtimeConfig'

/*
  dataroom's module, with the same exports (`getKeycloak`, `getFreshToken`, `logout`,
  `claims`, `isKeycloakConfigured`) so the rest of the client is interchangeable. `init` uses
  `check-sso` with a timeout instead of `login-required`, so a sign-in service that does not
  answer gives a "try again" screen rather than a hung page (ADR 0020). The branching on the
  answer lives in bootstrap.ts.
*/

let instance: Keycloak | null = null

/** The Keycloak client. Authorization code + PKCE (S256), no client secret in the browser. */
export function getKeycloak(): Keycloak {
  if (instance === null) {
    const { keycloak: config } = runtimeConfig()
    instance = new Keycloak({ url: config.url, realm: config.realm, clientId: config.clientId })
  }
  return instance
}

/**
 * True when Keycloak is configured. With no server URL the SPA runs in STUB MODE: it
 * sends no token and relies on the API's development stub. A function, not a
 * constant, because the answer is not known until the runtime configuration loads.
 */
export function isKeycloakConfigured(): boolean {
  return Boolean(runtimeConfig().keycloak.url)
}

/** Parsed access-token claims we care about. */
export interface TokenClaims {
  sub?: string
  preferred_username?: string
  email?: string
  name?: string
  realm_access?: { roles?: string[] }
}

export const INIT_TIMEOUT_MS = 6_000

export type InitOutcome = 'authenticated' | 'anonymous' | 'unreachable'

/**
 * check-sso with a timeout. Resolves `authenticated` when Keycloak answered with a
 * session, `anonymous` when it answered without one, and `unreachable` when it did not
 * answer within the timeout or threw (DNS failure, no network, a 5xx). The caller
 * decides what each means; this module does not redirect.
 */
export async function initKeycloak(timeoutMs = INIT_TIMEOUT_MS): Promise<InitOutcome> {
  if (!isKeycloakConfigured()) {
    return 'anonymous'
  }

  const init = getKeycloak().init({
    onLoad: 'check-sso',
    pkceMethod: 'S256',
    // The CSP forbids frames (frame-ancestors 'none' on us; Keycloak sets its own), so
    // the status iframe cannot work. The refresh timer below covers session expiry.
    checkLoginIframe: false,
    silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    // A full redirect from inside the silent check would loop on a third-party-cookie block.
    silentCheckSsoFallback: false,
  })

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<'unreachable'>((resolve) => {
    timer = setTimeout(() => resolve('unreachable'), timeoutMs)
  })

  try {
    const result = await Promise.race([
      init.then((ok): InitOutcome => (ok ? 'authenticated' : 'anonymous')),
      timeout,
    ])
    return result
  } catch {
    return 'unreachable'
  } finally {
    clearTimeout(timer)
  }
}

let refreshTimer: ReturnType<typeof setInterval> | undefined

/** Keeps the token fresh. A failed refresh is a sign-in, as dataroom. */
export function startTokenRefresh(onLost: () => void = () => void getKeycloak().login()): void {
  stopTokenRefresh()
  refreshTimer = setInterval(() => {
    void getKeycloak()
      .updateToken(70)
      .catch(() => onLost())
  }, 60_000)
}

export function stopTokenRefresh(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = undefined
  }
}

/**
 * A token guaranteed fresh for at least `minValiditySeconds`, or undefined in stub mode. A
 * failed refresh means the session is gone and the answer is the login page, as in dataroom.
 */
export async function getFreshToken(minValiditySeconds = 30): Promise<string | undefined> {
  if (!isKeycloakConfigured()) {
    return undefined
  }
  const keycloak = getKeycloak()
  if (!keycloak.authenticated) {
    return keycloak.token
  }
  try {
    await keycloak.updateToken(minValiditySeconds)
  } catch {
    void keycloak.login()
    return undefined
  }
  return keycloak.token
}

export function login(): void {
  if (isKeycloakConfigured()) {
    void getKeycloak().login({ redirectUri: window.location.href })
  }
}

export function logout(): void {
  stopTokenRefresh()
  if (isKeycloakConfigured()) {
    void getKeycloak().logout({ redirectUri: window.location.origin })
  }
}

export function claims(): TokenClaims {
  if (!isKeycloakConfigured()) {
    return {}
  }
  return (getKeycloak().tokenParsed ?? {}) as TokenClaims
}

/** Test seam. */
export function resetKeycloakForTests(): void {
  stopTokenRefresh()
  instance = null
}
